import React, { ReactNode, UIEvent, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useColor } from '~/hooks/server/color'
import { useUser, useService, useSystem } from '~/hooks/server/sync'
import { useArchivedToolQuery } from '~/hooks/server/tool'

import System from '~/models/system'
import { GetMenuOpen } from '~/modules/persist/menu/selectors'
import { UpdateNavigationScroll } from '~/modules/menu/actions'
import { CloseMenu, OpenMenu } from '~/modules/persist/menu/actions'
import { GetNaviActive } from '~/modules/menu/selectors'

import { ServiceTourStep, useServiceTourPositionSync } from '~/common/define/tour'
import { isEmptyModel, notEmptyModel } from '~/common/utils/model'
import { sortModels } from '~/common/utils/sort'
import { GeneralIconTag } from '~/common/define/icon'
import { notice } from '~/common/define/notice'
import { getHeight } from '~/common/utils/rect'
import {
  archivedPath,
  fileTopPath,
  serviceBasePath,
  spaceBasePath,
  systemBasePath,
  systemNewPath
} from '~/common/define/path'
import Navigation from '~/components/molecules/navigation/Navigation'
import NavigationBorderFrame from '~/components/molecules/navigation/NavigationBorderFrame'
import NavigationMain from '~/components/molecules/navigation/main/NavigationMain'
import NavigationLinkRow from '~/components/molecules/navigation/NavigationLinkRow'
import SpaceNavigationMenus from '~/components/layout/navigation/SpaceNavigationMenus'
import { NavigationMainItemVar } from '~/components/molecules/navigation/main/NavigationMainItem'
import NavigationScroll from '~/components/molecules/navigation/NavigationScroll'
import NavigationHeader from '~/components/molecules/navigation/NavigationHeader'
import NavigationCover from '~/components/molecules/navigation/NavigationCover'

const SpaceNavigation = () => {
  const {t} = useTranslation()
  const user = useUser()
  const service = useService()
  const system = useSystem()
  const currentSpace = notEmptyModel(system) ? system : service
  const color = useColor()
  const ref = useRef()

  const dispatch = useDispatch()
  const open = useSelector(GetMenuOpen)
  const naviActive = useSelector(GetNaviActive)
  const {tools: archivedTools} = useArchivedToolQuery(service, system)
  const fileRef = useServiceTourPositionSync(ServiceTourStep.FILE)

  const onClickCover = useCallback(() => {
    notice(t('navigation:cover_annotation'), dispatch)
  }, [t, dispatch])

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    dispatch(UpdateNavigationScroll(event.currentTarget.scrollTop))
  }
  if ((!user || !service)) {
    return <></>
  }

  const systemItems = notEmptyModel(service) ? sortModels<System>(service.systems).map((system): NavigationMainItemVar => {
    return {
      id: system.id,
      text: system.name,
      link: systemBasePath({
        ...system,
        service
      }),
      iconProps: {
        image: system.icon_image,
        tag: system.icon_tag,
        color: system.color,
      },
      color: system.color,
    }
  }) : []

  const systemList: ReactNode =
      <NavigationMain
        title={t('navigation:sys.title')} iconTag={GeneralIconTag.SERVER} color={service.color} items={systemItems}
        newLink={systemNewPath(service)} newTooltipText={t('navigation:sys.new')}
        newLinkText={service.systems.length > 0 ? null : t('navigation:sys.create') }
      />

  return (
    <Navigation color={color} open={open} onClickKnob={() => dispatch(open ? CloseMenu() : OpenMenu())}>
      <NavigationHeader
        mainTitleProps={{
          title: currentSpace.name,
          link: spaceBasePath(currentSpace),
          color,
          iconProps: {
            image: currentSpace.icon_image,
            tag: currentSpace.icon_tag,
            fillColor: currentSpace.color
          },
        }}
        subTitlePros={notEmptyModel(system) ? {
          title: service.name,
          link: serviceBasePath(service),
          color: service.color,
          iconProps: {
            image: service.icon_image,
            tag: service.icon_tag,
            fillColor: service.color
          },
        } : null}
        subNode={notEmptyModel(system) ? systemList : null}
      />
      <NavigationScroll onScroll={onScroll}>
        {isEmptyModel(system) &&
          <NavigationBorderFrame borderColor={color} bottom borderRef={ref}>
            {systemList}
          </NavigationBorderFrame>
        }
        <NavigationBorderFrame borderColor={color} bottom>
          <SpaceNavigationMenus color={color} service={service} system={system} systemHeight={getHeight(ref.current)}/>
        </NavigationBorderFrame>
        {(archivedTools && archivedTools.length > 0) &&
          <NavigationBorderFrame borderColor={color} bottom>
            <NavigationLinkRow text={t('navigation:archive')} iconTag={GeneralIconTag.ARCHIVE} link={archivedPath(currentSpace)} color={color}/>
          </NavigationBorderFrame>
        }
      </NavigationScroll>
      <NavigationBorderFrame borderColor={color} top>
        <NavigationLinkRow text={t('navigation:file')} iconTag={GeneralIconTag.FILE} link={fileTopPath(currentSpace)} color={color} linkRef={fileRef}/>
      </NavigationBorderFrame>
      <NavigationCover show={!naviActive} onClick={onClickCover}/>
    </Navigation>
  )
}

export default SpaceNavigation